import { GeckoRiskAssessment, RolodexEntryType } from 'types'

/**
 * Because risk score ascends in greater health, but risk class ascends in worst health,
 * we need to reflect the risk class across the scale to get the risk score
 * reflection = (num_of_points + 1) - risk_class
 */
export function getRiskScoreFromRiskClass(
  riskClass: number | undefined
): number | undefined {
  if (riskClass === undefined || riskClass === 0) {
    return undefined
  }
  return 5 - riskClass
}

export function getRiskClassFromRiskScore(
  riskScore: number | undefined
): number | undefined {
  if (riskScore === undefined) {
    return undefined
  }

  if (riskScore < 1) {
    return 1
  }

  return 5 - riskScore
}

/**
 * Gets the date value to use for comparing risk assessments.
 * Prefers risk_date (year), falls back to updated_at timestamp.
 */
export function getRiskAssessmentDate(ra: GeckoRiskAssessment): number {
  return (
    (ra.data.risk_date ?? 0) || new Date(ra.updated_at ?? '').getTime() || 0
  )
}

/**
 * Finds the latest risk assessment from an array of risk assessments.
 * Compares by risk_date first, then updated_at as fallback.
 */
export function getLatestRiskAssessment(
  riskAssessments: readonly GeckoRiskAssessment[]
): GeckoRiskAssessment | undefined {
  return riskAssessments.reduce<GeckoRiskAssessment | undefined>(
    (latest, ra) => {
      if (!latest) return ra
      return getRiskAssessmentDate(ra) >= getRiskAssessmentDate(latest)
        ? ra
        : latest
    },
    undefined
  )
}

/**
 * Finds the latest risk assessment for each unit from an array of risk assessments.
 * @returns Map of unit ID to the latest GeckoRiskAssessment for that unit
 */
export function getLatestRiskAssessmentByUnit(
  riskAssessments: readonly GeckoRiskAssessment[]
): Map<string, GeckoRiskAssessment> {
  const latestByUnit = new Map<string, GeckoRiskAssessment>()
  for (const ra of riskAssessments) {
    const unitId = ra.tags[RolodexEntryType.UNIT]
    if (!unitId) continue
    const current = latestByUnit.get(unitId)
    if (
      !current ||
      getRiskAssessmentDate(ra) >= getRiskAssessmentDate(current)
    ) {
      latestByUnit.set(unitId, ra)
    }
  }
  return latestByUnit
}
